"use client";

import { useRef } from "react";
import Container from "@/components/ui/Container";
import SectionHeading from "@/components/ui/SectionHeading";
import Button from "@/components/ui/Button";
import GradientText from "@/components/ui/GradientText";
import CtaBand from "@/components/sections/CtaBand";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import {
  CheckCircle2,
  Server,
  Layers,
  GitBranch,
  Shield,
  Landmark,
  Gamepad2,
  HeartPulse,
  Truck,
  Building2,
  Scale,
} from "lucide-react";

/* ─── Data ────────────────────────────────────────────────────────────────── */

const heroStats = [
  { value: "5B+", label: "API requests processed" },
  { value: "50+", label: "Networks supported" },
  { value: "<90ms", label: "p99 global latency" },
  { value: "99.95%", label: "Uptime SLA" },
];

const trustedBy = [
  "Skoda", "CoinDCX", "Shido", "Gems Pocket", "Boudicea", "Triskel", "Coin Hub",
];

const whoWeArePoints = [
  "A unified infrastructure layer spanning 50+ networks: Ethereum, Solana, Bitcoin, Base, BSC, Avalanche, Cosmos, Polkadot, TON, TRON, and beyond. One API key, one dashboard, one SLA.",
  "Zero vendor fragmentation. A single contractual relationship replaces multiple providers, eliminating procurement overhead, billing complexity, and operational risk at the protocol level.",
  "SOC 2 Type II certified, with multi-cloud deployment across AWS, GCP, and Azure. Carrier-grade distribution across global edge availability zones.",
];

const services = [
  {
    icon: Server,
    title: "Nodes-as-a-Service",
    description:
      "Dedicated, shared, and archive RPC nodes across 50+ networks. JSON-RPC and WebSocket endpoints with sub-90ms p99 latency, enterprise-grade auto-scaling, and global edge distribution. Built to sustain the throughput demands of production protocols.",
    accent: "purple" as const,
  },
  {
    icon: Layers,
    title: "Rollup-as-a-Service",
    description:
      "Deploy sovereign L2 rollup networks on Arbitrum Orbit, OP Stack, Polygon CDK, or zkSync. Instanodes manages sequencer operations, prover infrastructure, and data availability. Your protocol retains full control of the execution environment.",
    accent: "purple" as const,
  },
  {
    icon: GitBranch,
    title: "Appchain Deployment",
    description:
      "Purpose-configured application-specific blockchains, deployed in days not months. Custom VM, governance modules, bridge architecture, block explorer, and tokenomics. Delivered as a fully managed, production-ready appchain stack.",
    accent: "purple" as const,
  },
  {
    icon: Shield,
    title: "Validator Nodes",
    description:
      "Institutional-grade proof-of-stake validator operations with MEV optimization, slashing protection, and automated failover. Earn protocol rewards without assuming the operational burden of running and maintaining validator infrastructure.",
    accent: "purple" as const,
  },
];

const industries = [
  {
    icon: Landmark,
    title: "Finance & DeFi",
    description:
      "Dedicated high-throughput RPC infrastructure for exchanges, lending protocols, and digital asset custodians. Archive node access for audit-grade data retrieval, with low-latency endpoints calibrated for real-time settlement and order execution.",
  },
  {
    icon: Gamepad2,
    title: "Gaming & Metaverse",
    description:
      "High-TPS L2 rollups and appchains engineered for on-chain game economies. Sub-second finality for NFT issuance, marketplace settlement, and play-to-earn reward distribution at scale.",
  },
  {
    icon: HeartPulse,
    title: "Healthcare",
    description:
      "Private permissioned appchains with granular access controls for sensitive clinical data. Immutable on-chain audit logs built to satisfy regulatory compliance requirements and enable secure cross-provider data exchange.",
  },
  {
    icon: Truck,
    title: "Supply Chain",
    description:
      "Multi-chain provenance infrastructure tracking goods from origin to end customer. Validator nodes that anchor enterprise supply chain records to public settlement layers for tamper-evident, court-admissible verification.",
  },
  {
    icon: Building2,
    title: "Real Estate & RWA",
    description:
      "Application-specific blockchains purpose-built for real-world asset tokenization. Fractional ownership, compliance-enforced transfer restrictions, and institutional-grade on-chain securities registries.",
  },
  {
    icon: Scale,
    title: "Government & Enterprise",
    description:
      "Permissioned validator infrastructure for identity registries, public records management, and enterprise compliance workflows. Procurement-ready architecture that meets institutional governance and independent audit standards.",
  },
];

const connectivity = [
  {
    number: "01",
    title: "Global Edge PoPs",
    description:
      "Node clusters deployed across US-East, EU-West, and AP-Southeast availability zones. Requests resolve to the nearest healthy endpoint. P99 latency holds below 90ms regardless of origin geography.",
  },
  {
    number: "02",
    title: "Automatic Failover",
    description:
      "Every request path has a redundant route. Node failures trigger automatic traffic rerouting within 500ms. No manual escalation, no service disruption, no incident tickets.",
  },
  {
    number: "03",
    title: "Elastic Capacity",
    description:
      "Infrastructure scales automatically to match your protocol's demand. No provisioning requests, no capacity planning cycles. You pay for utilization, not headroom.",
  },
];

const pillars = [
  {
    title: "Infrastructure Should Be Invisible",
    body: "Production blockchain infrastructure must be silent, reliable, and relentless. Five billion API requests processed across 50+ networks. That throughput exists because protocol teams and enterprises trust us with the infrastructure underlying their most critical operations. We have not let them down.",
  },
  {
    title: "Continuous Protocol Coverage",
    body: "We launched with Ethereum node operations. Today, our network spans 50+ chains: Solana, Bitcoin, Base, Avalanche, Cosmos, Polkadot, TON, TRON, Celo, Mantle, Fantom, Tezos, and beyond. Rollup deployments cover Arbitrum Orbit, OP Stack, Polygon CDK, and zkSync. When a new L2 reaches mainnet, our clients typically have production RPC access within the first week.",
  },
  {
    title: "Built Around Your Operations",
    body: "Protocols at CoinDCX, Shido, and Skoda run their production blockchain infrastructure through Instanodes. Onboarding is not the end of our engagement. It is the beginning. We provide 24/7 infrastructure monitoring, dedicated support channels, and pre-mainnet architecture reviews. SOC 2 Type II certified, deployed across AWS, GCP, and Azure for enterprise-grade distribution across global availability zones.",
  },
];

/* ─── Page Content (client component with scroll animations) ─────────────── */

export default function AboutUsContent() {
  const heroRef = useRef<HTMLElement>(null);
  const whoWeAreRef = useRef<HTMLElement>(null);
  const servicesRef = useRef<HTMLElement>(null);
  const industriesRef = useRef<HTMLElement>(null);
  const infraRef = useRef<HTMLElement>(null);
  const pillarsRef = useRef<HTMLElement>(null);

  useScrollReveal(heroRef);
  useScrollReveal(whoWeAreRef, 0.15);
  useScrollReveal(servicesRef, 0.08);
  useScrollReveal(industriesRef, 0.1);
  useScrollReveal(infraRef, 0.15);
  useScrollReveal(pillarsRef, 0.1);

  return (
    <>
      {/* ── Hero ── */}
      <section ref={heroRef} className="section-pad relative overflow-hidden">
        <div aria-hidden className="bg-glow-hero pointer-events-none absolute inset-0 -z-10" />
        <Container>
          <div className="reveal mx-auto max-w-4xl text-center">
            <h1 className="mb-6 text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Institutional-Grade Blockchain Infrastructure,{" "}
              <GradientText>Operated at Protocol Scale.</GradientText>
            </h1>
            <p className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-text-secondary">
              Instanodes is a web3 infrastructure provider delivering production-ready RPC nodes, validator infrastructure, rollups, and appchains
              across 50+ networks. Built for protocol teams, financial institutions, and
              enterprise blockchain deployments that cannot afford downtime.
            </p>
            <Button href="/contact-us" variant="primary" size="lg">
              Connect With Us
            </Button>
          </div>

          {/* Stats band */}
          <div className="mx-auto mt-16 grid max-w-3xl grid-cols-2 gap-6 sm:grid-cols-4">
            {heroStats.map((s, i) => (
              <div key={s.label} className={`reveal reveal-delay-${i + 1} text-center`}>
                <p className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">{s.value}</p>
                <p className="mt-1 text-xs text-text-muted">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Trusted by */}
          <div className="reveal reveal-delay-5 mt-12 text-center">
            <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.18em] text-text-muted">
              Trusted by leading protocols and institutions
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
              {trustedBy.map((name) => (
                <span key={name} className="text-sm font-medium text-text-muted/70 transition-colors hover:text-text-secondary">
                  {name}
                </span>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ── Who We Are ── */}
      <section ref={whoWeAreRef} className="section-pad bg-bg-surface">
        <Container>
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="reveal">
              <SectionHeading
                eyebrow="Our Story"
                title="Who We Are"
                align="left"
                className="mb-6"
              />
              <p className="mb-8 text-base leading-relaxed text-text-secondary">
                Instanodes is a web3 infrastructure provider specializing in enterprise blockchain deployments. We operate RPC
                nodes, validator nodes, rollups, and application-specific blockchains across 50+
                networks (Ethereum, Solana, Bitcoin, Base, BSC, Avalanche, Cosmos,
                Polkadot, TON, TRON, and more) on behalf of protocols, DeFi platforms, and enterprise
                teams, including CoinDCX, Shido, and Skoda. Your engineers focus on building.
                We ensure the infrastructure never becomes the bottleneck. SOC 2 Type II certified,
                with multi-cloud deployment across AWS, GCP, and Azure.
              </p>
              <Button href="/contact-us" variant="outline">
                Connect With Us
              </Button>
            </div>

            <ul className="space-y-4">
              {whoWeArePoints.map((point, i) => (
                <li key={point} className={`reveal reveal-delay-${i + 1} flex items-start gap-3`}>
                  <CheckCircle2
                    size={18}
                    aria-hidden
                    className="mt-0.5 shrink-0 text-brand-green"
                  />
                  <span className="text-base leading-relaxed text-text-secondary">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </section>

      {/* ── What We Do — stacked rows with accent border ── */}
      <section ref={servicesRef} className="section-pad">
        <Container>
          <div className="reveal mb-12 max-w-2xl">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-purple-light">
              Services
            </p>
            <h2 className="text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-4xl">
              From a single RPC Endpoint to a fully sovereign appchain.
            </h2>
          </div>

          <div className="space-y-4">
            {services.map((s, i) => (
              <div
                key={s.title}
                className={`reveal reveal-delay-${i + 1} group grid items-center gap-6 rounded-2xl border border-border-subtle bg-bg-card p-6 transition-all duration-300 hover:border-border-medium hover:bg-bg-elevated sm:grid-cols-[auto_1fr_1.2fr]`}
              >
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border"
                  style={{
                    borderColor: s.accent === "purple" ? "rgba(79,70,229,0.25)" : "rgba(16,185,129,0.25)",
                    background: s.accent === "purple" ? "rgba(79,70,229,0.08)" : "rgba(16,185,129,0.06)",
                  }}
                >
                  <s.icon
                    size={22}
                    aria-hidden
                    className="text-brand-purple-light"
                  />
                </div>
                <h3 className="text-xl font-semibold text-text-primary">{s.title}</h3>
                <p className="text-base leading-relaxed text-text-secondary">{s.description}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Industries — left intro + right compact list ── */}
      <section ref={industriesRef} className="section-pad bg-bg-surface">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1fr_1.5fr]">
            {/* Left: editorial intro */}
            <div className="reveal lg:sticky lg:top-32 lg:self-start">
              <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-purple-light">
                Industry Reach
              </p>
              <h2 className="mb-4 text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-4xl">
                One infrastructure layer,{" "}
                <span className="gradient-text">configured per vertical.</span>
              </h2>
              <p className="text-base leading-relaxed text-text-secondary">
                Every industry has distinct infrastructure requirements: microsecond latency
                for trading desks, strict data access controls for healthcare, sustained high TPS
                for gaming economies. The underlying infrastructure is the same. The configuration
                is what changes.
              </p>
            </div>

            {/* Right: compact industry rows */}
            <div className="grid gap-3 sm:grid-cols-2">
              {industries.map((ind, i) => (
                <div
                  key={ind.title}
                  className={`reveal reveal-delay-${Math.min(i + 1, 5)} flex gap-4 rounded-xl border border-border-subtle bg-bg-card p-5 transition-all duration-300 hover:border-border-medium`}
                >
                  <ind.icon
                    size={20}
                    aria-hidden
                    className="mt-0.5 shrink-0 text-brand-purple-light"
                  />
                  <div>
                    <h3 className="mb-1 text-base font-semibold text-text-primary">{ind.title}</h3>
                    <p className="text-sm leading-relaxed text-text-secondary">{ind.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ── Global Infrastructure — horizontal stats band + descriptions ── */}
      <section ref={infraRef} className="relative overflow-hidden py-16">
        <div aria-hidden className="bg-glow-center pointer-events-none absolute inset-0 -z-10 opacity-40" />
        <Container>
          {/* Stats row */}
          <div className="mb-12 grid grid-cols-2 gap-4 text-center sm:grid-cols-4">
            {[
              { value: "50+", unit: "", label: "Blockchains supported" },
              { value: "<90", unit: "ms", label: "p99 latency globally" },
              { value: "99.95", unit: "%", label: "Measured uptime" },
              { value: "4.8", unit: "s", label: "Avg deploy time" },
            ].map((s, i) => (
              <div key={s.label} className={`reveal reveal-delay-${i + 1}`}>
                <p className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                  {s.value}{s.unit && <span className="text-2xl text-text-muted sm:text-3xl">{s.unit}</span>}
                </p>
                <p className="mt-1 text-sm text-text-muted">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="reveal mx-auto mb-12 h-px w-full max-w-lg bg-gradient-to-r from-transparent via-border-medium to-transparent" />

          {/* Descriptions */}
          <div className="grid gap-10 md:grid-cols-3">
            {connectivity.map((item, i) => (
              <div key={item.number} className={`reveal reveal-delay-${i + 1}`}>
                <p className="mb-2 font-mono text-sm font-bold text-brand-purple-light">{item.number}</p>
                <h3 className="mb-2 text-lg font-semibold text-text-primary">{item.title}</h3>
                <p className="text-sm leading-relaxed text-text-secondary">{item.description}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Mission / Values — editorial prose with dividers ── */}
      <section ref={pillarsRef} className="section-pad bg-bg-surface">
        <Container size="narrow">
          <div className="space-y-12">
            {pillars.map((pillar, i) => (
              <div key={pillar.title} className="reveal">
                {i > 0 && (
                  <div className="mb-12 h-px bg-gradient-to-r from-transparent via-border-medium to-transparent" />
                )}
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-purple-light">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mb-4 text-2xl font-bold tracking-tight text-text-primary sm:text-3xl">
                  {pillar.title}
                </h3>
                <p className="max-w-2xl text-lg leading-[1.7] text-text-secondary">
                  {pillar.body}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Final CTA ── */}
      <CtaBand
        eyebrow="Partner with Instanodes"
        heading="Deploy Production Infrastructure Today."
        subheading="Get your first endpoint live in under 5 minutes. Enterprise plans and custom SLAs available on request."
        primaryCta={{ label: "Talk to Our Team", href: "/contact-us" }}
        secondaryCta={{ label: "View Pricing", href: "/pricing" }}
      />
    </>
  );
}
